import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { LOG_ACTIONS, logAction } from '@namespace/analytics';
import {
  useCookieManagementEnabled,
  useGeneralRoutes,
  useOtherSettings
} from '@namespace/cms';
import { structuringErrors } from '@namespace/forms';
import {
  COOKIES_KEYS,
  deleteCriticalMessageStorage,
  getCookie,
  getLocalStorageValue,
  LOCAL_STORAGE_KEYS,
  SESSION_STORAGE_KEYS,
  setCookie,
  setIsShowAfterLoginNotifications,
  setLocalStorageValue,
  setSessionStorageValue
} from '@namespace/helpers';
import { useI18n } from '@namespace/i18n';
import { useHistory } from '@namespace/router';
import { USER_ROLES } from '../../constants';
import { UserContext } from '../../context';
import { useUserFetch } from './hooks/useUserFetch';
import { getBlockErrorMessage } from './getBlockErrorMessage';
import { getRGLimitsRedirectUrl } from './getRGLimitsRedirectUrl';

const useLogin = ({
  onSuccess = () => {},
  onError = () => {},
  loadCaptchaToken = () => {},
  fromPath,
  isModalLogin
} = {}) => {
  const { t, f } = useI18n();
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [userData, userActions] = useContext(UserContext);
  const { accountStatuses = {} } = userData;
  const {
    isNeedSetupLimits,
    isPassportInVerification,
    isPassportVerifiedByRisk,
    isAdditionalInformationRequired
  } = accountStatuses;

  const nextPageCached = useRef();
  const { FETCH_LOGIN, FETCH_USER_DATA, SET_USER_AUTHORIZED_THROUGH_MODAL } =
    userActions;
  const { push } = useHistory();
  const cookieManagementEnabled = useCookieManagementEnabled();
  const {
    cookieManagement,
    showLogOnTimer = false,
    isNeedSetupDepositLimitCheck = false,
    userSettings = {}
  } = useOtherSettings();
  const { isMandatoryLimitPageRequiresUserVerification = false } = userSettings;
  const { mandatoryLimit, profileDocuments } = useGeneralRoutes();
  const { disableSendingAffiliateCookies = false } = cookieManagement || {};
  const affiliateInSessionStorage = getLocalStorageValue(
    LOCAL_STORAGE_KEYS.AFFILIATE_COOKIES
  );
  const affiliateParams =
    cookieManagementEnabled &&
    !disableSendingAffiliateCookies &&
    affiliateInSessionStorage
      ? affiliateInSessionStorage
      : null;
  const [userStore] = useContext(UserContext);
  const { role } = userStore;

  const userFetch = useUserFetch({ onSuccess });

  useEffect(() => {
    if (role === USER_ROLES.USER && isNeedSetupDepositLimitCheck) {
      const redirectUrl = getRGLimitsRedirectUrl({
        isAdditionalInformationRequired,
        isMandatoryLimitPageRequiresUserVerification,
        isNeedSetupLimits,
        isPassportInVerification,
        isPassportVerifiedByRisk,
        mandatoryLimit,
        nextPageCached,
        profileDocuments
      });

      push(redirectUrl);
    }
  }, [
    isNeedSetupLimits,
    isNeedSetupDepositLimitCheck,
    mandatoryLimit,
    profileDocuments,
    push,
    role,
    fromPath,
    isPassportInVerification,
    isPassportVerifiedByRisk,
    isAdditionalInformationRequired,
    isMandatoryLimitPageRequiresUserVerification,
    onSuccess,
    userData
  ]);

  const login = useCallback(
    (credentials) => {
      setError(null);
      onError(null);
      if (credentials !== null) {
        const {
          username,
          password,
          captcha,
          showReCaptchaSection,
          reCaptchaParams,
          nextPage,
          withAutoLogin = true
        } = credentials;

        (async () => {
          setIsLoading(true);
          try {
            setSessionStorageValue(
              SESSION_STORAGE_KEYS.SHOW_PRIVACY_POLICY_BANNER,
              false
            );

            if (withAutoLogin) {
              const reCaptchaData = showReCaptchaSection
                ? reCaptchaParams
                : await loadCaptchaToken();
              await FETCH_LOGIN({
                username,
                password,
                captcha,
                affiliateParams,
                reCaptchaData
              });
            }

            await userFetch.fetch();

            if (isModalLogin) {
              // we need this to trigger page context update for protected routes
              // see libs/layout/src/components/PageRouterWrapper/index.js:87
              SET_USER_AUTHORIZED_THROUGH_MODAL(false);
            }

            deleteCriticalMessageStorage();
            if (showLogOnTimer && !getCookie(COOKIES_KEYS.LOG_ON)) {
              setCookie(COOKIES_KEYS.LOG_ON, f.getDateTime().toMillis(), {
                expires: 30
              });
            }
            setLocalStorageValue(
              LOCAL_STORAGE_KEYS.IS_DISPLAY_ONBOARDING_POPUP,
              true
            );

            setIsShowAfterLoginNotifications(true);

            nextPageCached.current = nextPage;
            if (!isNeedSetupDepositLimitCheck && nextPage) {
              push(nextPage);
            }
            setSuccess(false);
          } catch (e) {
            console.error(e);
            if (!e.errorCode) {
              if (nextPage) {
                push(nextPage);
              }
              logAction(LOG_ACTIONS.LOGIN_RELOAD);
              // important! needed at least for bug with different sessions in socket
              window.location.reload();
            } else {
              const { blockCode, mainAccountEmail } = e?.data ?? {};

              const blockErrorMessage = blockCode
                ? getBlockErrorMessage({
                    blockCode,
                    mainAccountEmail,
                    t
                  })
                : undefined;

              onError(
                e.errorCode,
                structuringErrors(e, 'accountingMessages.', blockErrorMessage)
              );

              setError(e.errorCode);
              setSuccess(false);
            }
          }
          setIsLoading(false);
        })();
      }
    },
    [FETCH_LOGIN, FETCH_USER_DATA, isModalLogin, onError, onSuccess, push]
  );

  return {
    login,
    success,
    error,
    setError,
    setSuccess,
    isLoading
  };
};

export default useLogin;
